import { z } from 'zod';

import { PublicUserDTO } from './me';
import { ApiOk, DeviceCode, I18nText, IsoDate, Platform, UserCode } from './primitives';
import { SessionDTO } from './sessions';

/** Lifecycle of a `pairing_codes` row (RTDB only mirrors the first four). */
export const PairingCodeStatus = z.enum(['pending', 'approved', 'denied', 'expired', 'consumed']);
export type PairingCodeStatus = z.infer<typeof PairingCodeStatus>;

/** `POST /tv/pair/start` (no auth) */
export const PairStartBody = z.object({
  deviceId: z.string().min(8).max(128),
  platform: Platform,
  model: z.string().max(120).optional(),
  appVersion: z.string().max(40).optional(),
});
export type PairStartBody = z.infer<typeof PairStartBody>;

export const PairStartResponse = z.object({
  deviceCode: DeviceCode,
  userCode: UserCode,
  verificationUrl: z.string(),
  qrUrl: z.string(),
  expiresAt: IsoDate,
  /** RTDB path to subscribe to, e.g. `/pairing/<deviceCode>`. */
  pollPath: z.string(),
});
export type PairStartResponse = z.infer<typeof PairStartResponse>;

/** `POST /tv/pair/exchange` (no auth, one-shot) */
export const PairExchangeBody = z.object({ deviceCode: DeviceCode });
export type PairExchangeBody = z.infer<typeof PairExchangeBody>;

export const PairExchangeApproved = z.object({
  status: z.literal('approved'),
  customToken: z.string().min(1),
  session: SessionDTO,
  user: PublicUserDTO,
});
export type PairExchangeApproved = z.infer<typeof PairExchangeApproved>;

export const PairExchangePending = z.object({
  status: z.enum(['pending', 'denied', 'expired']),
});
export type PairExchangePending = z.infer<typeof PairExchangePending>;

/** 200 with the approved shape; 202 with `{ status }` otherwise. */
export const PairExchangeResponse = z.discriminatedUnion('status', [
  PairExchangeApproved,
  PairExchangePending,
]);
export type PairExchangeResponse = z.infer<typeof PairExchangeResponse>;

/**
 * `POST /tv/pair/resume` (no auth, rate-limited per IP): a TV that was paired before opens a
 * new session for the same host without showing the QR again, as long as its last session
 * ended by running out of time (not a logout, an operator close or another host pairing it).
 * `unavailable` → the TV shows the pairing code as usual.
 */
export const PairResumeBody = z.object({ deviceId: z.string().min(8).max(128) });
export type PairResumeBody = z.infer<typeof PairResumeBody>;

export const PairResumeUnavailable = z.object({ status: z.literal('unavailable') });
export type PairResumeUnavailable = z.infer<typeof PairResumeUnavailable>;

export const PairResumeResponse = z.discriminatedUnion('status', [
  PairExchangeApproved,
  PairResumeUnavailable,
]);
export type PairResumeResponse = z.infer<typeof PairResumeResponse>;

/** `GET /tv/pair/precheck?code=` (no auth) */
export const PairPrecheckQuery = z.object({ code: UserCode });
export type PairPrecheckQuery = z.infer<typeof PairPrecheckQuery>;
export type PairPrecheckQueryInput = z.input<typeof PairPrecheckQuery>;

export const PendingGrantDTO = z.object({
  productName: I18nText,
  kind: z.enum(['plan', 'addon']),
});
export type PendingGrantDTO = z.infer<typeof PendingGrantDTO>;

export const PairPrecheckResponse = z.object({
  valid: z.boolean(),
  expired: z.boolean(),
  /** Only when `valid`. */
  device: z
    .object({
      id: z.string(),
      grants: z.array(PendingGrantDTO),
    })
    .optional(),
});
export type PairPrecheckResponse = z.infer<typeof PairPrecheckResponse>;

/** `POST /tv/pair/approve` (auth, non-anonymous) */
export const PairApproveBody = z.object({ userCode: UserCode });
export type PairApproveBody = z.infer<typeof PairApproveBody>;
export type PairApproveBodyInput = z.input<typeof PairApproveBody>;

export const PairApproveResponse = z.object({ session: SessionDTO });
export type PairApproveResponse = z.infer<typeof PairApproveResponse>;

/** `details` of the `402 subscription_required` error from approve. */
export const SubscriptionRequiredDetails = z.object({ redirect: z.string() });
export type SubscriptionRequiredDetails = z.infer<typeof SubscriptionRequiredDetails>;

/** `POST /tv/pair/deny` (auth) */
export const PairDenyBody = z.object({ userCode: UserCode });
export type PairDenyBody = z.infer<typeof PairDenyBody>;
export type PairDenyBodyInput = z.input<typeof PairDenyBody>;

export const PairDenyResponse = ApiOk;
export type PairDenyResponse = z.infer<typeof PairDenyResponse>;

/** Pairing code lifetime (docs/02 §1: start + 10 min). */
export const PAIRING_CODE_TTL_MS = 10 * 60 * 1000;
/** Session lifetime (docs/02 §1: started_at + 6 h). */
export const SESSION_TTL_MS = 6 * 60 * 60 * 1000;

/**
 * `POST /tv/telemetry` (TV token): player diagnostics in batches, written to the API's logs
 * (`tv_telemetry`, Workers observability / `wrangler tail`). Nothing is stored in D1.
 */
export const TvTelemetryEvent = z.object({
  /** Epoch ms on the TV. */
  t: z.number().int().nonnegative(),
  type: z.string().min(1).max(40),
  d: z.record(z.string(), z.unknown()).optional(),
});
export type TvTelemetryEvent = z.infer<typeof TvTelemetryEvent>;

export const TvTelemetryBody = z.object({
  events: z.array(TvTelemetryEvent).min(1).max(200),
  appVersion: z.string().max(40).optional(),
  ua: z.string().max(300).optional(),
});
export type TvTelemetryBody = z.infer<typeof TvTelemetryBody>;
export const TvTelemetryResponse = ApiOk;
export type TvTelemetryResponse = z.infer<typeof TvTelemetryResponse>;
